import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
interface AuthPageProps {
  onBack: () => void;
  onSuccess: () => void;
}
type AuthMode = 'login' | 'signup';
export function AuthPage({
  onBack,
  onSuccess
}: AuthPageProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {
    signIn,
    signUp,
    error: authError,
    clearError
  } = useAuth();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    clearError();
    if (mode === 'signup' && password !== confirmPassword) {
      setFormError('Las contraseñas no coinciden.');
      return;
    }
    if (password.length < 6) {
      setFormError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setIsLoading(true);
    try {
      if (mode === 'login') {
        const success = await signIn(email, password);
        if (success) {
          onSuccess();
        }
      } else {
        const success = await signUp(email, password);
        if (success) {
          setShowConfirmation(true);
        }
      }
    } catch {
      setFormError('Ocurrió un error inesperado. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };
  const switchMode = () => {
    setMode(prev => prev === 'login' ? 'signup' : 'login');
    setFormError(null);
    setConfirmPassword('');
    clearError();
    setShowConfirmation(false);
  };
  const displayError = formError || authError;
  // Confirmation screen after signup
  if (showConfirmation) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-id="element-540">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center border border-gray-100" data-id="element-541">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-542">
            <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-543" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-544">
            ¡Cuenta creada!
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-545">
            Enviamos un enlace de confirmación a{' '}
            <strong className="text-gray-900" data-id="element-546">{email}</strong>. Revisa tu correo
            (y la carpeta de spam) para activar tu cuenta.
          </p>
          <button onClick={() => {
          setShowConfirmation(false);
          setMode('login');
          setPassword('');
          setConfirmPassword('');
        }} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-547">
            Ir a Iniciar Sesión
          </button>
          <button onClick={onBack} className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-id="element-548">
            Volver al inicio
          </button>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8" data-id="element-549">
      {/* Back button */}
      <div className="fixed top-6 left-6 z-10" data-id="element-550">
        <button onClick={onBack} className="flex items-center text-gray-500 hover:text-blue-600 transition-colors text-sm font-medium" data-id="element-551">
          <ArrowLeft className="w-4 h-4 mr-1.5" data-id="element-552" />
          Volver
        </button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-553">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6" data-id="element-554">
          <div className="w-10 h-8 bg-yellow-400 rounded-lg flex items-center justify-center shadow-sm" data-id="element-555">
            <span className="font-bold tracking-tight text-[16px] text-gray-900" data-id="element-556">
              EAR
            </span>
          </div>
          <span className="font-bold tracking-tight text-[16px] text-gray-900" data-id="element-557">
            Import{' '}
            <span style={{
            color: '#d42323'
          }} data-id="element-558">
              Auto
            </span>
          </span>
        </div>

        <h2 className="text-center text-2xl font-bold text-gray-900" data-id="element-559">
          {mode === 'login' ? 'Bienvenido de nuevo' : 'Crea tu cuenta'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-500" data-id="element-560">
          {mode === 'login' ? 'Ingresa para ver el estado de tus solicitudes' : 'Regístrate para dar seguimiento a tus importaciones'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-561">
        <div className="bg-white py-8 px-6 shadow-xl shadow-gray-200/40 rounded-2xl sm:px-10 border border-gray-100" data-id="element-562">
          {/* Tab switcher */}
          <div className="flex mb-8 bg-gray-100 rounded-lg p-1" data-id="element-563">
            <button onClick={() => mode !== 'login' && switchMode()} className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${mode === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-564">
              Iniciar Sesión
            </button>
            <button onClick={() => mode !== 'signup' && switchMode()} className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${mode === 'signup' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-565">
              Crear Cuenta
            </button>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit} data-id="element-566">
            {/* Email */}
            <div data-id="element-567">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-568">
                Correo electrónico
              </label>
              <div className="relative" data-id="element-569">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-570">
                  <Mail className="h-5 w-5 text-gray-400" data-id="element-571" />
                </div>
                <input id="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors" placeholder="tucorreo@ejemplo.com" data-id="element-572" />
              </div>
            </div>

            {/* Password */}
            <div data-id="element-573">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-574">
                Contraseña
              </label>
              <div className="relative" data-id="element-575">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-576">
                  <Lock className="h-5 w-5 text-gray-400" data-id="element-577" />
                </div>
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors" placeholder="Mínimo 6 caracteres" minLength={6} data-id="element-578" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" data-id="element-579">
                  {showPassword ? <EyeOff className="h-4 w-4" data-id="element-580" /> : <Eye className="h-4 w-4" data-id="element-581" />}
                </button>
              </div>
            </div>

            {/* Confirm Password (signup only) */}
            <AnimatePresence data-id="element-582">
              {mode === 'signup' && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden" data-id="element-583">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-584">
                    Confirmar contraseña
                  </label>
                  <div className="relative" data-id="element-585">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-586">
                      <Lock className="h-5 w-5 text-gray-400" data-id="element-587" />
                    </div>
                    <input id="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required={mode === 'signup'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors" placeholder="Repite tu contraseña" minLength={6} data-id="element-588" />
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Error display */}
            <AnimatePresence data-id="element-589">
              {displayError && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} className="rounded-lg bg-red-50 p-4 border border-red-100" data-id="element-590">
                  <div className="flex gap-3" data-id="element-591">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-592" />
                    <p className="text-sm text-red-700" data-id="element-593">{displayError}</p>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Submit */}
            <button type="submit" disabled={isLoading} className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/20" data-id="element-594">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-595" /> : mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-400" data-id="element-596">
            Al continuar, aceptas ser contactado por EARIA Import Auto.
          </p>
        </div>
      </div>
    </div>;
}